import React from 'react';
import {
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';
import { useSessionManager } from './useSessionManager';
import { useTokenRefresh } from './useTokenRefresh';
import { LogoutButton } from '../components/LogoutButton';
import { DemoCatalog } from '../components/DemoCatalog';

/**
 * Inner component rendered inside AuthenticatedTemplate.
 * Hooks must be called unconditionally, so session management and
 * token refresh are wired into this always-rendered authenticated shell.
 */
const AuthenticatedShell: React.FC = () => {
  const { accounts } = useMsal();
  const { isSessionValid } = useSessionManager();
  useTokenRefresh();

  const userEmail = accounts[0]?.username || '';

  if (!isSessionValid) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <p className="auth-loading__text">Session expired. Redirecting to login...</p>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__brand">
          <span className="app-header__logo">☁️</span>
          <span className="app-header__title">AWS Demo Platform</span>
        </div>
        <div className="app-header__actions">
          <span className="app-header__user">{userEmail}</span>
          <LogoutButton />
        </div>
      </header>
      <main className="app-main">
        <div className="page-header">
          <h1 className="page-header__title">Demo Catalog</h1>
          <p className="page-header__desc">Browse, search, and play AWS project demos</p>
        </div>
        <DemoCatalog />
      </main>
    </div>
  );
};

/**
 * AuthenticatedContent handles the login flow:
 * - If authenticated: renders the main application shell with session management
 * - If unauthenticated: triggers a login redirect via Auth Code + PKCE
 * - While in progress: shows a loading state
 */
export const AuthenticatedContent: React.FC = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <p className="auth-loading__text">Authenticating...</p>
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedShell />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="login-page">
          <div className="login-card">
            <div className="login-card__logo">☁️</div>
            <h1 className="login-card__title">AWS Demo Platform</h1>
            <p className="login-card__subtitle">
              Sign in with your Royal Cyber account to browse and play AWS project demos.
            </p>
            <button onClick={handleLogin} className="login-card__btn">
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
                <rect width="10" height="10" fill="#f25022"/>
                <rect x="11" width="10" height="10" fill="#7fba00"/>
                <rect y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>
            <p className="login-card__footer">Royal Cyber employees only (@royalcyber.com)</p>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};
